'use client';

import SvgChevrondown from '@common/__generated__/icon/SvgChevrondown';
import { Button } from '@common/components';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';

import { UnauthQueryOptions } from '@/api';
import { Image, LoadingPageTemplate } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';

import { Rating } from './Rating';

interface ReviewDetailSessionPortalProps {
  open?: boolean;
  onClose?: () => void;
  reviewId?: number;
}

export const ReviewDetailSessionPortal = ({
  open,
  onClose,
  reviewId,
}: ReviewDetailSessionPortalProps) => {
  const [isAnswerExpanded, setIsAnswerExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const { data: review, isLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-review/{reviewId}']({
      path: {
        reviewId: reviewId!,
      },
    }),
    enabled: !!reviewId && Boolean(open),
    select: (res) => res.context,
  });

  const isPhotoReview =
    review?.fileUrlList && review.fileUrlList.length > 0;

  if (isLoading) {
    return <LoadingPageTemplate blur fadeIn />;
  }

  return (
    <ResponsiveDialog
      modalHeader={isPhotoReview ? '포토후기' : '후기'}
      opened={!!open && !!reviewId}
      onClose={onClose}
      className={{ modal: 'max-w-[800px]' }}
    >
      <div className="relative flex size-full flex-col">
        {isPhotoReview && (
          <div className="relative aspect-square w-full shrink-0">
            <div className="size-full overflow-hidden" ref={emblaRef}>
              <div className="flex h-full">
                {review?.fileUrlList.map((url, index) => (
                  <div
                    key={index}
                    className="relative aspect-square min-w-0 flex-[0_0_100%]"
                  >
                    <Image
                      src={url}
                      alt="답례품 후기 이미지"
                      width={850}
                      height={850}
                      className="size-full object-cover object-center"
                    />
                  </div>
                ))}
              </div>
            </div>
            {review?.fileUrlList && review.fileUrlList.length > 1 && (
              <div className="absolute bottom-0 right-0 mb-16 ml-auto mr-16 w-fit rounded-pill bg-Gray-black/20 px-8 py-2 font-600 text-Gray-white typo-caption2">
                {selectedIndex + 1} / {review.fileUrlList.length}
              </div>
            )}
          </div>
        )}
        <div className="flex flex-col p-20">
          <Rating value={review?.rating ?? 0} size={16} className="mb-8" />
          <div className="flex items-center gap-4 font-400 text-Gray-550 typo-caption1">
            {review?.nickname}
            <div className="mb-6 h-10 w-1 bg-Gray-300" />
            {dayjs(review?.createdAt).format('YYYY.MM.DD')}
          </div>
          <p className="mb-16 font-400 text-Gray-600 typo-body4">
            {review?.productName}
          </p>
          <p className="whitespace-pre-line font-500 text-Gray-800 typo-body3">
            {review?.contents}
          </p>
        </div>

        {review?.answer ? (
          <div className="mx-20 mt-16 flex flex-col rounded-medium bg-Gray-25 p-20">
            <div className="flex items-center justify-between pb-4">
              <p className="font-600 text-Gray-700 typo-body4">판매자</p>
              {review?.answerAt ? (
                <p className="font-400 text-Gray-550 typo-caption1">
                  {dayjs(review?.answerAt).format('YY.MM.DD')}
                </p>
              ) : null}
            </div>
            <p className="whitespace-pre-line pb-8 font-400 text-Gray-700 typo-body4">
              {review?.answer.length > AnswerExpandedPoint ? (
                <>
                  {isAnswerExpanded
                    ? review?.answer
                    : `${review?.answer.slice(0, AnswerExpandedPoint)}...`}
                  <Button
                    className="mt-8 flex size-fit items-center gap-4 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAnswerExpanded((prev) => !prev);
                    }}
                    theme="sub-text"
                  >
                    <p className="font-600 text-Gray-550 typo-caption1">
                      {isAnswerExpanded ? '접기' : '더보기'}
                    </p>
                    <SvgChevrondown
                      className={cn(
                        'size-12 text-Gray-550 transition-transform duration-300',
                        {
                          '-rotate-180': isAnswerExpanded,
                        },
                      )}
                    />
                  </Button>
                </>
              ) : (
                review?.answer
              )}
            </p>
          </div>
        ) : null}
      </div>
    </ResponsiveDialog>
  );
};

const AnswerExpandedPoint = 60;
